import { createContext, useContext, useMemo, type ReactNode } from 'react';

import {
  LOCALE_META,
  MESSAGE_FALLBACK_LOCALE,
  directionOf,
  formatDate,
  formatList,
  formatNumber,
  translate,
  type Direction,
  type Locale,
  type MessageKey,
  type MessageParams,
  type Messages,
} from '@bitwize/i18n-messages';

export interface I18nValue {
  locale: Locale;
  dir: Direction;
  messages: Messages;
  /** Resolve a message key. Missing keys render the key itself, visibly. */
  t: (key: MessageKey | (string & {}), params?: MessageParams) => string;
  formatNumber: (value: number, options?: Intl.NumberFormatOptions) => string;
  formatDate: (value: Date | string | number, options?: Intl.DateTimeFormatOptions) => string;
  formatList: (items: readonly string[]) => string;
}

const I18nContext = createContext<I18nValue | null>(null);

export interface I18nProviderProps {
  locale: Locale;
  messages: Messages;
  /** Consulted when the active catalogue has no entry for a key. */
  fallbackMessages?: Messages | undefined;
  children: ReactNode;
}

export function I18nProvider({ locale, messages, fallbackMessages, children }: I18nProviderProps) {
  const value = useMemo<I18nValue>(() => {
    const onMissing =
      import.meta.env.DEV && locale !== MESSAGE_FALLBACK_LOCALE
        ? (key: string) => console.warn(`[i18n] missing ${locale} message: ${key}`)
        : undefined;

    return {
      locale,
      dir: directionOf(locale),
      messages,
      t: (key, params) =>
        translate(messages, key, params, { fallback: fallbackMessages, onMissing }),
      formatNumber: (n, options) => formatNumber(locale, n, options),
      formatDate: (d, options) => formatDate(locale, d, options),
      formatList: (items) => formatList(locale, items),
    };
  }, [locale, messages, fallbackMessages]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/**
 * Throws when used outside a provider rather than falling back to English.
 * A silent English fallback is how an untranslated component reaches the Arabic
 * site and survives review.
 */
export function useI18n(): I18nValue {
  const value = useContext(I18nContext);
  if (!value) {
    throw new Error('useI18n was called outside I18nProvider');
  }
  return value;
}

/** Shorthand for the common case. */
export function useTranslate(): I18nValue['t'] {
  return useI18n().t;
}

export function useLocale(): Locale {
  return useI18n().locale;
}

export function useDirection(): Direction {
  return useI18n().dir;
}

export { LOCALE_META };
